import { useCallback, useEffect, useRef, useState } from "react";
import type { Step } from "./viewModel.js";

/**
 * A log that is stuck to the bottom.
 *
 * It follows new activity until the user scrolls up, and re-locks the moment
 * they scroll back down. `following` is the single source for that, so a fade
 * or a jump control driven by it cannot appear while the view is locked — those
 * are the "you have scrolled away" state, not decoration.
 *
 * This lives in `lib/` because both skins render the same build activity and
 * neither may import the other. The markup is presentation and belongs to a
 * skin; this is behaviour, and a second hand-written copy of it would be a
 * second set of the three timing bugs the comments below record.
 *
 * @param deps what growth to watch. Anything that changes the scroller's height
 *   without adding a scroll event — a new row, a row's timer ticking, output
 *   arriving under the running step.
 */
export function useStickToBottom(deps: readonly unknown[]) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [following, setFollowing] = useState(true);
  // Mirrored in a ref because the append effect has to read it before React has
  // committed a state change from a scroll that happened in the same frame.
  const followingRef = useRef(true);
  const [scrolled, setScrolled] = useState(false);

  const measure = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const distance = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
    const next = distance < FOLLOW_MARGIN_PX;
    followingRef.current = next;
    setFollowing(next);
    setScrolled(scroller.scrollTop > 8);
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.addEventListener("scroll", measure, { passive: true });
    return () => scroller.removeEventListener("scroll", measure);
  }, [measure]);

  useEffect(() => {
    // Locked: ride the growth down. Deliberately no re-measure afterwards — the
    // view is at the bottom by construction, and reading layout back here caught
    // the pane mid-reflow and flashed the jump control at a pinned feed.
    if (followingRef.current) {
      scrollTo(scrollerRef.current);
      return;
    }
    // Scrolled away: new content only changes how much is hidden below, and
    // growth on its own fires no scroll event.
    measure();
    // The caller's list is spread rather than passed as one value, so a change
    // to any single member re-runs this. Its length is fixed by the call site,
    // which is what React requires of a dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measure, ...deps]);

  /**
   * Back to the bottom, and locked again.
   *
   * Snaps rather than animating: a smooth scroll keeps firing scroll events all
   * the way down, each one re-measuring a view that is still in transit, so the
   * control the user just pressed blinks at them until the animation lands.
   */
  const jumpToLatest = useCallback(() => {
    followingRef.current = true;
    setFollowing(true);
    scrollTo(scrollerRef.current);
  }, []);

  return { scrollerRef, following, scrolled, jumpToLatest };
}

/**
 * All the way down, by scroll offset rather than by scrolling a marker element
 * into view.
 *
 * The two are not the same, and the difference is a bug. Both panes end with
 * generous bottom padding so the last row has clear space under it rather than
 * stopping against the frame — and `scrollIntoView` on a marker above that
 * padding aligns the MARKER with the bottom edge, leaving the padding below the
 * fold. The pane then sits ~69px short of its own bottom, which is past
 * `FOLLOW_MARGIN_PX`, so the very act of following unlocked the follow and put
 * the jump control on a feed that was plainly pinned.
 */
function scrollTo(scroller: HTMLElement | null) {
  if (!scroller) return;
  scroller.scrollTop = scroller.scrollHeight;
}

/**
 * Every way a build log grows, as a dependency list.
 *
 * A row arriving is the obvious one and the only one that is enough. A step's
 * timer advances in place, a repeat bumps its count, and a running command
 * prints under its own row — each grows the scroller without adding a row, and
 * none of them fires a scroll event. Missing any one of them lets a long
 * command scroll its own output out of view.
 *
 * Shared rather than written out at each call site, because it is the same list
 * for both skins and a log that silently stops following is not a failure
 * anyone notices in a test.
 */
export function logGrowth(steps: Step[], isTerminal: boolean): readonly unknown[] {
  const last = steps.at(-1);
  return [isTerminal, steps.length, last?.count, last?.elapsedMs, last?.io?.output.length];
}

/**
 * How far up counts as having left the bottom.
 *
 * Wide enough to absorb a wheel notch, a trackpad flick that overshoots, and the
 * sub-pixel scroll height a growing row reports mid-render. Measured tightly, a
 * feed that is plainly pinned flickers the jump control at itself as it grows —
 * which is the bug this margin exists to prevent.
 */
const FOLLOW_MARGIN_PX = 64;
